/**
 * One month's deliverables for one client, laid out as a board — the month
 * on screen, which the client page can step back and forth.
 *
 * The old studio board was a wall: one column per promise ("4 Reels"), the
 * pieces for it pinned underneath, and an empty outline for each one not yet
 * started, so a gap is visible from across the room. This is that wall. A
 * column's empty outlines are the promise minus the work that exists, which is
 * the number worth seeing — not "2 of 4 posted" but "two not even started".
 *
 * Ongoing services (Google Ads management) have no pieces to count and show as
 * a single card; so do one-time jobs. Work filed without a promise gets a
 * column of its own at the end, because it is real work the plan does not show.
 */

import { BarChart3, FileText, Film, Globe, Image, Sparkles, type LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Dashboard, Piece, Progress, Stage } from '../../api';
import { useAppData } from '../../context/AppDataContext';
import { api, apiErrorMessage } from '../../lib/api';
import { cn } from '../../lib/cn';
import { monthName, shortDate } from '../../lib/format';

const STAGE: Record<Stage, { label: string; dot: string }> = {
  briefed: { label: 'Briefed', dot: '#91919c' },
  shot: { label: 'Shot', dot: '#3b7dde' },
  editing: { label: 'Editing', dot: '#d98e1c' },
  internal_review: { label: 'Review', dot: '#8a5cf5' },
  with_client: { label: 'With client', dot: '#fc2265' },
  scheduled: { label: 'Scheduled', dot: '#2c63b8' },
  posted: { label: 'Posted', dot: '#2f9e57' }
};

export function iconFor(metric: string): LucideIcon {
  const m = metric.toLowerCase();
  if (/reel|video|short|youtube/.test(m)) return Film;
  if (/website|site|web/.test(m)) return Globe;
  if (/ads?\b|seo|aeo|marketing|campaign|analysis/.test(m)) return BarChart3;
  if (/article|linkedin|blog|newsletter/.test(m)) return FileText;
  if (/post|pin|carousel|story|static/.test(m)) return Image;
  return Sparkles;
}

interface Lane {
  key: string;
  progress: Progress | null;
  pieces: Piece[];
}

export function DeliverablesBoard({ clientId, color }: { clientId: string; color?: string }) {
  const { content, directory, month } = useAppData();
  const [view, setView] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  // `content` changes whenever the app refreshes after a save, which is when
  // the counts here can have moved.
  useEffect(() => {
    let cancelled = false;
    api
      .get<Dashboard>(`/clients/${encodeURIComponent(clientId)}`, { month })
      .then((v) => {
        if (!cancelled) {
          setView(v);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(apiErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, content, month]);

  if (error !== null && view === null) {
    return <p className="text-sm text-shell-text">Could not load this board: {error}</p>;
  }
  if (view === null) {
    return (
      <div className="flex gap-3 overflow-hidden" aria-label="Loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-48 w-56 shrink-0 animate-pulse rounded-2xl bg-shell-border/50" />
        ))}
      </div>
    );
  }

  const lanes: Lane[] = [...view.progress]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((p) => ({
      key: p.commitment_id,
      progress: p,
      pieces: view.pieces.filter((x) => x.commitment_id === p.commitment_id)
    }));
  const loose = view.pieces.filter(
    (x) => x.commitment_id === null || !view.progress.some((p) => p.commitment_id === x.commitment_id)
  );
  if (loose.length > 0) lanes.push({ key: 'loose', progress: null, pieces: loose });

  const owner = (id: string | null) =>
    id === null ? null : directory.find((p) => p.id === id)?.name.split(' ')[0] ?? null;

  if (lanes.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-shell-border p-6 text-center text-sm text-shell-text">
        Nothing is promised for {monthName(view.month)} yet. Set what this client gets every month, and it
        shows up here.
      </div>
    );
  }

  return (
    <div
      className="scrollbar-thin -mx-1 overflow-x-auto rounded-2xl border border-shell-border p-4"
      style={{
        // Dot grid, like a whiteboard.
        backgroundImage: 'radial-gradient(var(--color-shell-border) 1.2px, transparent 1.2px)',
        backgroundSize: '18px 18px'
      }}
    >
      <div className="flex min-w-max items-start gap-4">
        {lanes.map((lane) => {
          const p = lane.progress;
          const Icon = p === null ? Sparkles : iconFor(p.metric);
          const service = p !== null && (p.unit === 'active' || p.unit === 'project');
          const started = lane.pieces.length;
          const outlines = p === null || service ? 0 : Math.max(0, p.target_count - started);
          const done = p === null ? 0 : p.delivered;
          const pct = p === null || p.target_count === 0 ? 0 : Math.min(100, (done / p.target_count) * 100);

          return (
            <section key={lane.key} className="w-56 shrink-0 space-y-2.5 sm:w-60">
              {/* The promise itself, as the heading note of its column. */}
              <header
                className="rounded-2xl border border-shell-border bg-canvas-surface p-3 shadow-card"
                style={{ borderTop: `3px solid ${color ?? 'var(--color-brand-500)'}` }}
              >
                <div className="flex items-center gap-2">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-brand-500/10 text-brand-500">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h4 className="min-w-0 flex-1 truncate text-sm font-semibold text-canvas-fg">
                    {p === null ? 'Extra work' : p.metric}
                  </h4>
                  {p !== null && !service && (
                    <span className="text-sm font-semibold tabular-nums text-canvas-fg">
                      {done}
                      <span className="font-normal text-shell-text">/{p.target_count}</span>
                    </span>
                  )}
                </div>
                {p === null ? (
                  <p className="mt-1.5 text-xs text-shell-text">Filed without a promise to count against.</p>
                ) : service ? (
                  <p className="mt-1.5 text-xs text-shell-text">
                    {p.unit === 'active'
                      ? p.target_count > 1
                        ? `Ongoing · ${p.target_count} running`
                        : 'Ongoing this month'
                      : 'One-time job'}
                  </p>
                ) : (
                  <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-shell-border">
                    <div className="h-full rounded-full bg-success-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                )}
              </header>

              {lane.pieces.map((piece) => {
                const stage = STAGE[piece.stage];
                const who = owner(piece.owner_id);
                return (
                  <article
                    key={piece.id}
                    className={cn(
                      'rounded-xl border border-shell-border bg-canvas-surface px-3 py-2.5 shadow-card',
                      piece.stage === 'posted' && 'opacity-75'
                    )}
                  >
                    <p className="line-clamp-2 text-sm font-medium text-canvas-fg">{piece.title}</p>
                    <div className="mt-1.5 flex items-center gap-2 text-xs text-shell-text">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: stage.dot }} />
                        {stage.label}
                      </span>
                      {piece.due !== null && <span className="tabular-nums">· {shortDate(piece.due)}</span>}
                      {who !== null && <span className="ml-auto truncate">{who}</span>}
                    </div>
                  </article>
                );
              })}

              {Array.from({ length: outlines }, (_, i) => (
                <div
                  key={`gap-${i}`}
                  className="rounded-xl border-2 border-dashed border-shell-border px-3 py-3 text-xs text-shell-text"
                >
                  Not started
                </div>
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
